import React, { useCallback, useEffect, useState } from 'react';
import { engagementApi, googleAdsApi, leadApi, marketingApi, xAdsApi } from '../services/api';
import { LeadRow, SurfaceReport, SurfaceSource } from '../types';
import { istToday, pct } from '../lib';
import DateRangeBar from '../components/DateRangeBar';
import NumberStrip, { StripItem } from '../components/NumberStrip';
import { Section, Dash, Failed, Loading, Note, Out, PageFrame, Strip, Th, td, tdBig, tdOurs, tr } from '../components/PlatformBits';
import { Cuts, SourcesTable, SurfaceLeads, n, prettyDevice, prettyIntent, surfaceBadge } from '../components/SurfaceBits';
import { rowKey, rowName } from '../components/SourcesList';
import { USD_INR, platformOf } from '../components/PlatformsCard';

/** The money site as an acquisition funnel: visitor → tapped → WhatsApp
 *  opened → became a lead. The site's button ids arrive verbatim and are
 *  rendered by rule (prettyIntent), never by a map kept here. */

const CLARITY = 'https://clarity.microsoft.com/projects/view/yf84xmgws2/dashboard';

const WebsitePage: React.FC = () => {
  const [range, setRange] = useState({ from: istToday(), to: istToday() });
  const [data, setData] = useState<SurfaceReport | null>(null);
  const [names, setNames] = useState<Map<string, LeadRow>>(new Map());
  const [spend, setSpend] = useState<{ meta: Map<string, number>; x: number | null; google: number | null }>({ meta: new Map(), x: null, google: null });
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setData(null); setError(null);
    engagementApi.surface('website', range.from, range.to).then((r) => {
      setData(r);
      return leadApi.byIds(r.leads.map((l) => l.lead_id)).then((rows) => setNames(new Map(rows.map((l) => [l.id, l]))));
    }).catch((err: any) => setError(err?.response?.data?.detail || err.message));
    // Spend is context: each platform's total, so a paid source can be priced.
    marketingApi.campaignSpend(range.from, range.to).then((r) => setSpend((s) => ({ ...s, meta: new Map(r.campaigns.map((c) => [c.campaign_id, c.spend])) }))).catch(() => undefined);
    xAdsApi.report(range.from, range.to).then((r) => setSpend((s) => ({ ...s, x: r.posts.reduce((a, p) => a + p.spend_micro, 0) / 1e6 * USD_INR }))).catch(() => undefined);
    googleAdsApi.report(range.from, range.to).then((r) => setSpend((s) => ({ ...s, google: r.campaigns.reduce((a, c) => a + c.cost_micros, 0) / 1e6 }))).catch(() => undefined);
  }, [range]);

  useEffect(() => { load(); }, [load]);

  if (error) return <Failed what="the website" error={error} />;

  const f = data?.funnel ?? {};
  const strip: StripItem[] = data ? [
    { v: f.visits_conducted ?? 0, k: 'site visits', cls: 'visited' },
    { v: f.visits_scheduled ?? 0, k: 'scheduled', cls: 'gold' },
    { v: n(f.new_leads), k: 'became leads', p: f.tapped ? `${pct(f.new_leads ?? 0, f.tapped)} of tappers` : undefined },
    { v: n(f.tapped), k: 'tapped WhatsApp', p: `${n(f.handoff_switched)} opened` },
    { v: n(f.visitors), k: 'visitors' },
  ] : [];

  // Meta spend is per campaign, so it sits on the row; X and Google are one
  // number each, so they sit on the platform's group heading.
  const spendOf = (s: SurfaceSource): number | null =>
    platformOf(s) === 'meta' && s.campaign && spend.meta.has(s.campaign) ? spend.meta.get(s.campaign)! : null;
  const groupSpend = (key: string): number | null => (key === 'ads:x' ? spend.x : key === 'ads:google' ? spend.google : null);

  const lost = (f.tapped ?? 0) - (f.new_leads ?? 0);
  const leadRows = (data?.leads ?? []).filter((l) => !selected || (l.tag ?? 'direct') === selected);
  const selectedSource = selected ? data?.sources.find((s) => rowKey(s) === selected) : undefined;

  return (
    <PageFrame
      title="Website"
      badge={surfaceBadge('website')}
      subline={<>bhuvanafortunegreens.com · <Out href="https://bhuvanafortunegreens.com/">Open the site</Out> · <Out href={CLARITY}>Clarity recordings</Out></>}
      bar={<DateRangeBar from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} />}
    >
      {!data && <Loading what="the website" />}

      {data && (
        <>
          <Strip>
            <NumberStrip items={strip} />
            {lost > 0 && (
              <div className="text-[12.5px] text-graphite mt-4">
                <b className="text-charcoal">{n(lost)} tapped WhatsApp but never sent the message</b> — only the send makes a lead.
              </div>
            )}
          </Strip>

          <SourcesTable sources={data.sources} spendOf={spendOf} groupSpend={groupSpend} onSelect={setSelected} selected={selected} />

          {data.buttons && (
            <Section title="Buttons">
              <table className="w-full min-w-[760px] border-collapse">
                <thead><tr><Th left>Button</Th><Th>Taps</Th><Th>People</Th><Th>WhatsApp opened</Th><Th ours>Leads</Th><Th ours>Per 100 tappers</Th></tr></thead>
                <tbody>
                  {data.buttons.map((b) => (
                    <tr key={b.intent} className={tr}>
                      <td className="px-3 py-2.5"><span className="font-semibold">{prettyIntent(b.intent)}</span><div className="text-[11px] text-graphite/80 font-mono">{b.intent}</div></td>
                      <td className={tdBig}>{n(b.taps)}</td>
                      <td className={`${td} text-right`}>{n(b.tappers)}</td>
                      <td className={`${td} text-right`}>{b.switched ? <>{n(b.switched)}<span className="text-graphite text-[11.5px] ml-1.5">{pct(b.switched, b.tappers)}</span></> : <Dash />}</td>
                      <td className={tdOurs}>{b.new_leads || <Dash />}</td>
                      <td className={`${td} text-right`}>{b.new_leads ? (b.new_leads / b.tappers * 100).toFixed(1) : <Dash />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Note>A lead is credited to the tap within the day before it was created.</Note>
            </Section>
          )}

          {data.after_tap && (
            <Section title="After the tap" note={data.after_tap.handoff_ms != null ? `WhatsApp opens in ${(data.after_tap.handoff_ms / 1000).toFixed(1)} s — the link works; the loss is inside WhatsApp` : undefined}>
              <table className="w-full min-w-[820px] border-collapse">
                <thead><tr>
                  <Th left>Device</Th><Th>Tapped</Th><Th ours>Sent</Th><Th>Rate</Th><Th>Came back</Th><Th>Tapped again</Th><Th>Gone</Th><Th>Tap → send</Th>
                </tr></thead>
                <tbody>
                  {data.after_tap.by_device.map((r) => (
                    <tr key={r.device} className={tr}>
                      <td className="px-3 py-2.5 font-semibold whitespace-nowrap">{prettyDevice(r.device)}</td>
                      <td className={tdBig}>{n(r.tappers)}</td>
                      <td className={tdOurs}>{r.became_lead || <Dash />}</td>
                      <td className={`${td} text-right ${r.tappers >= 20 && r.became_lead / r.tappers < 0.12 ? 'text-brick font-bold' : ''}`}>{pct(r.became_lead, r.tappers)}</td>
                      <td className={`${td} text-right`}>{r.came_back || <Dash />}</td>
                      <td className={`${td} text-right`}>{r.tapped_again || <Dash />}</td>
                      <td className={`${td} text-right text-graphite`}>{r.neither || <Dash />}</td>
                      <td className={`${td} text-right`}>{r.tap_to_lead_s != null ? `${Math.round(r.tap_to_lead_s)} s` : <Dash />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Note>Sent, came back, tapped again and gone are exclusive — every tapper lands in one. A low rate on one device with the handoff working points inside WhatsApp on that device.</Note>
            </Section>
          )}

          <Cuts cuts={data.cuts} verb="Visitors" />

          <SurfaceLeads
            title="Leads from the website"
            leads={leadRows}
            names={names}
            filter={selected ? { label: selectedSource ? rowName(selectedSource) : selected, onClear: () => setSelected(null) } : null}
            extraHeaders={['Button', 'Tap → send', 'Device']}
            extra={(l) => [
              l.tap_intent ? prettyIntent(l.tap_intent) : <Dash />,
              l.tap_to_lead_s != null && l.tap_to_lead_s >= 0 ? `${Math.round(l.tap_to_lead_s)} s` : <Dash />,
              l.device ? prettyDevice(l.device) : <Dash />,
            ]}
          />
        </>
      )}
    </PageFrame>
  );
};

export default WebsitePage;
